// src/services/enseignantService.js
import useAxios from '../utils/useAxios';
import { tenantURL } from '../utils/config';

// Préfixe du module pointage (comme dans adminService)
const POINTAGE_PREFIX = 'pointage/';

export const useEnseignantService = () => {
  const axiosInstance = useAxios();

  // ─── PROFIL ────────────────────────────────────────────────────────────
  const getMonProfil = async (slug) => {
    try {
      // mon-profil/ (MonCompteView) : lecture de request.user, sans restriction
      // de rôle — contrairement à mon-compte/ qui est réservée aux PARENT.
      const response = await axiosInstance.get(tenantURL(slug, 'mon-profil/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement du profil:', error);
      throw error;
    }
  };

  const updateProfil = async (slug, data) => {
    try {
      const response = await axiosInstance.patch(tenantURL(slug, 'mon-profil/'), data);
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      throw error;
    }
  };

  // ─── CLASSES ────────────────────────────────────────────────────────────
  const getMesClasses = async (slug) => {
    try {
      // Utiliser le même endpoint que l'admin
      // Le backend filtre les classes selon l'utilisateur connecté
      const response = await axiosInstance.get(tenantURL(slug, 'classes/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      throw error;
    }
  };

  const getClasseDetail = async (slug, classeId) => {
    try {
      // Utiliser le même endpoint que l'admin
      const response = await axiosInstance.get(tenantURL(slug, `classes/${classeId}/`));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de la classe:', error);
      throw error;
    }
  };

  // ─── ÉLÈVES ────────────────────────────────────────────────────────────
  const getElevesParClasse = async (slug, classeId) => {
    try {
      // Utiliser l'endpoint existant pour les élèves
      const response = await axiosInstance.get(tenantURL(slug, `eleves/?classe_id=${classeId}`));
      // La réponse peut être paginée ou non
      return response.data.results || response.data || [];
    } catch (error) {
      console.error('Erreur lors du chargement des élèves:', error);
      throw error;
    }
  };

  // ─── POINTAGE : PRÉSENCES DU JOUR ──────────────────────────────────────
  const getPresencesClasse = async (slug, classeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/`)}?${queryParams}`
        : `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classeId}/`)}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des présences:', error);
      throw error;
    }
  };

  // ─── POINTAGE : HISTORIQUE ─────────────────────────────────────────────
  const getHistoriquePointages = async (slug, params = {}) => {
    try {
      const { classe, ...rest } = params;
      const queryParams = new URLSearchParams(rest).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classe}/historique/`)}?${queryParams}`
        : `${tenantURL(slug, `${POINTAGE_PREFIX}presences/${classe}/historique/`)}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement de l\'historique:', error);
      throw error;
    }
  };

  // ─── POINTAGE : ENREGISTRER LES PRÉSENCES ─────────────────────────────
  const enregistrerPresences = async (slug, data) => {
    try {
      // Utiliser l'endpoint existant pour l'enregistrement rapide
      const response = await axiosInstance.post(
        tenantURL(slug, `${POINTAGE_PREFIX}enregistrer-presences-rapide/`),
        data
      );
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement des présences:', error);
      throw error;
    }
  };

  // ─── POINTAGE : ÉLÈVES NON POINTÉS ────────────────────────────────────
  const getElevesNonPointes = async (slug, classeId, params = {}) => {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = queryParams
        ? `${tenantURL(slug, `${POINTAGE_PREFIX}eleves-non-pointes/${classeId}/`)}?${queryParams}`
        : `${tenantURL(slug, `${POINTAGE_PREFIX}eleves-non-pointes/${classeId}/`)}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des élèves non pointés:', error);
      throw error;
    }
  };

  // ─── PARAMÈTRES ────────────────────────────────────────────────────────
  const getParametres = async (slug) => {
    try {
      const response = await axiosInstance.get(tenantURL(slug, 'parametres/'));
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
      throw error;
    }
  };

  return {
    // Profil
    getMonProfil,
    updateProfil,

    // Classes
    getMesClasses,
    getClasseDetail,

    // Élèves
    getElevesParClasse,

    // Pointage
    getPresencesClasse,
    getHistoriquePointages,
    enregistrerPresences,
    getElevesNonPointes,

    // Paramètres
    getParametres,
  };
};